import { dayjs } from 'utils/DateUtils';
import { useSelector, useDispatch } from 'react-redux';
import Grid from '@mui/material/Grid';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import { Alert, Link } from '@mui/material';
import { getInitialsName } from 'utils/String';
import { RootState } from 'store';
import { FOOTER_MENU_OPTIONS } from 'constant/NavigationConstant';
import { ENV_CONFIG } from 'config/env';
import { useLogout } from 'services/v1/Auth';
import { resetHomeNavigatinState } from 'store/reducer/homeReducer';
import { setTenantNavigation } from 'store/reducer/uiReducer';
import { logout } from 'store/reducer/authReducer';

const gridBannerStyle = {
  backgroundImage: `url('/images/auth-banner.jpg')`,
  backgroundRepeat: 'no-repeat',
  backgroundBlendMode: 'color',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  height: '100%',
};

const greetingBoldStyle = {
  marginBottom: 1,
  fontWeight: '500',
};

const gridContentContainerStyle = {
  backgroundColor: '#F8FAFF',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
};

const contentContainerStyle = {
  my: 5,
  mx: 4,
  px: 4,
  py: 4,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  backgroundColor: '#fff',
  borderRadius: '7px',
  boxShadow: '0px 1px 6px #C8CFE2',
};

export function NoAccessPage(): JSX.Element {
  const { profile } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const { mutate: logoutUser } = useLogout();

  const handleLogoutRedirect = () => {
    dispatch(resetHomeNavigatinState());
    setTimeout(() => {
      dispatch(setTenantNavigation(false));
      dispatch(logout());
      window.location.href = '/auth/login';
    }, 200);
  };

  const handleLogout = () => {
    logoutUser(undefined, {
      onSuccess: handleLogoutRedirect,
      onError: handleLogoutRedirect,
    });
  };

  return (
    <Grid container component='main' sx={{ height: '100vh' }}>
      <CssBaseline />

      <Grid
        item
        display='flex'
        flexDirection='column'
        paddingBottom={3}
        justifyContent='space-between'
        xs={5}
        sx={gridBannerStyle}
      >
        <Box width='100%' justifyContent='center' display='flex' sx={{ marginTop: 15 }}>
          <Stack direction='column' width='75%'>
            <Stack display='flex' justifyContent='center' flexDirection='row' mb={2}>
              <Avatar sx={{ bgcolor: '#00B3DC', width: 89, height: 89, fontSize: 37 }}>
                {getInitialsName(profile.name)}
              </Avatar>
            </Stack>
            <Typography component='h2' variant='h5' color='#fff' sx={greetingBoldStyle} textAlign='center'>
              {`Welcome ${profile.name}`}
            </Typography>
          </Stack>
        </Box>
        <Box width='100%' justifyContent='center' display='flex'>
          <Stack
            direction='row'
            width='75%'
            alignItems='center'
            justifyContent='flex-start'
            gap={3}
            height={80}
            divider={<Divider orientation='vertical' flexItem color='#fff' />}
          >
            <Stack direction='row' gap={1} mt={2}>
              <div>
                <img
                  src='/images/jazanz_logo.jpg'
                  alt='Jas ANZ Logo'
                  style={{
                    width: 'auto',
                    height: 60,
                  }}
                />
              </div>
              <div>
                <img
                  src='/images/iso_9001_certified.svg'
                  alt='ISO 9001 Certified'
                  style={{
                    background: 'white',
                    width: 'auto',
                    height: 60,
                  }}
                />
              </div>
            </Stack>
            <Stack>
              <Stack direction='row' spacing={2}>
                {FOOTER_MENU_OPTIONS.map((menu) => (
                  <Link
                    key={menu.label}
                    href={menu.link}
                    underline='hover'
                    target='_blank'
                    sx={{
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 'bold',
                      '&:hover': {
                        color: '#fff',
                      },
                    }}
                  >
                    {menu.label}
                  </Link>
                ))}
              </Stack>
              <Typography component='p' variant='subtitle1' color='#fff' sx={{ fontSize: 10, marginTop: 1 }}>
                {`©️ ${dayjs().year()} ARO Forecasting Pty Ltd All Rights Reserved.`}
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Grid>

      <Grid item xs={7} sx={gridContentContainerStyle}>
        <Box width={500} sx={contentContainerStyle}>
          <Box width={400} display='flex' justifyContent='space-between'>
            <Typography
              component='h5'
              variant='h5'
              color='#394798'
              sx={{
                marginBottom: 3,
              }}
            >
              No Access
            </Typography>

            <div>
              <img
                src='/images/main-logo.svg'
                alt='logo'
                style={{
                  width: '100px',
                  height: 'auto',
                }}
              />
            </div>
          </Box>
          <Box width={400}>
            <Alert severity='info'>
              It appears you do not have access to any resources. Please contact your system administrator to request
              access.
            </Alert>
            <Stack direction='row' spacing={2} justifyContent='center' width='100%' mt={2}>
              <Button
                variant='main-table-panel-border'
                fullWidth
                href={`mailto:${ENV_CONFIG.config.SUPPORT_EMAIL}?subject=Request Access&body=Hi Admin,%0D%0A%0D%0AI am requesting access to the Arotarget system.%0D%0A%0D%0AMy Username: ${profile.name}%0D%0A%0D%0AThanks.`}
              >
                Contact Admin
              </Button>
              <Button variant='main-table-panel' fullWidth onClick={handleLogout}>
                Logout
              </Button>
            </Stack>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
}
